import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cx } from "class-variance-authority";
import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import useResizeObserver from "use-resize-observer";
import { HStack } from "../composition/stack.layout";
import InputLabel from "../input-general/input-label";
import { normalizeLabelProp } from "../input-general/label-prop";
import ValidationMessage, {
  invalidInputOutlineClasses,
} from "../input-general/validation";
import { useHtmlId } from "../utils/useHtmlId";
import { IsItemSelected, OnSelectItem } from "./types";

const errors = {
  newItemHandlerWithoutTitle: `Found an onCreateNewItem handler but no new item title.

If you want to create new items you must provide a title for the new item using the "newItemOverride" prop, or enable the search bar by passing in "searchable".`,
  noNewItemHandlerForOveride: `Found a newItemOverride but no onCreateNewItem handler.`,
};

export interface SelectOption {
  label: string;
  value: string;
  key?: string;
  textClass?: string;
}

// For default/empty values
export const DUMMY_SELECT_OPTION = {
  label: "SHOULD NOT RENDER",
  value: '1234567890-==qwertyuiop[]\\ASDFGHJKL:"ZXCVBNM<>?',
};

export interface OptionRendererProps {
  disabled?: boolean;
  selected: boolean;
  item: SelectOption;
  icon?: () => React.ReactNode;
  textClass?: string;
  onSelect: (item: SelectOption) => void;
}

export interface CheckableOptionRendererProps extends OptionRendererProps {
  color?: "magenta" | "navy";
}

export const CheckableOption = (props: CheckableOptionRendererProps) => {
  const colorClass = props.color === "magenta" ? "bg-magenta" : "bg-navy";

  return (
    <DropdownMenu.CheckboxItem
      onSelect={(e) => {
        e.preventDefault();
        props.onSelect(props.item);
      }}
      checked={props.selected}
      onCheckedChange={(checked) => {
        props.onSelect(props.item);
      }}
    >
      <div
        className={cx(
          "flex w-full cursor-pointer items-center",
          "space-x-2 px-2 py-2",
          "hover:bg-friendly-eskimo",
          "rounded text-base",
          "focus:border-none focus:outline-none focus:ring-1 focus:ring-navy",
        )}
      >
        <div
          style={{
            width: 16,
            height: 16,
          }}
          className="flex-shrink-0 cursor-pointer overflow-hidden rounded border border-dublin-gray text-white"
        >
          <DropdownMenu.ItemIndicator
            className={cx(
              "h-full w-full",
              "flex items-center justify-center",
              props.selected === true && colorClass,
            )}
          >
            {props.selected === true && <FiCheck />}
          </DropdownMenu.ItemIndicator>
        </div>
        <span>{props.item.label}</span>
      </div>
    </DropdownMenu.CheckboxItem>
  );
};

export const BasicOption = (props: OptionRendererProps) => {
  if (props.disabled && !props.selected)
    return (
      <DropdownMenu.Item
        disabled={true}
        className={cx("group rounded", "cursor:not-allowed opacity-40")}
        id={props.item.value}
      >
        <div
          className={cx(
            "flex w-full cursor-not-allowed items-center text-sm",
            "space-x-2 px-3 py-2",
            "rounded text-base",
          )}
        >
          <span>{props.item.label}</span>
        </div>
      </DropdownMenu.Item>
    );
  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        e.stopPropagation();
        e.preventDefault();
        props.onSelect(props.item);
      }}
      className={cx(
        "group rounded",
        "focus:bg-friendly-eskimo focus:outline-none",
        "focus:text-navy",
      )}
      id={props.item.value}
    >
      <div
        className={cx(
          "flex w-full cursor-pointer items-center text-sm",
          "space-x-2 px-3 py-2",
          "rounded text-base",
          props.selected ? "text-navy" : "",
        )}
      >
        <span>{props.item.label}</span>
      </div>
    </DropdownMenu.Item>
  );
};

export interface SelectProps {
  isOptionDisabled?: (option: SelectOption) => boolean;
  // Basic use case props
  open: boolean;
  disabled?: boolean;
  align?: "end" | "start" | "center";
  side?: "top" | "bottom" | "left" | "right";

  options: SelectOption[];
  onOpenChange: (open: boolean) => void;
  onSelectItem?: OnSelectItem;
  onCreateNewItem?: (itemName: string, event: Event) => void;
  searchable?: boolean;
  children?: React.ReactNode;

  // Advanced customization use case props
  trigger?: () => React.ReactNode;
  width?: number | string;
  isItemSelected?: IsItemSelected;
  renderItem?: (item: SelectOption) => React.ReactNode;
  filterFunction?: (item: SelectOption, search: string) => boolean;
  contentProps?: DropdownMenu.MenuContentProps;
  fitTrigger?: boolean;
  hover?: boolean;
  newItemOverride?: {
    itemTitle?: string;
    label?: string;
  };
  placeholder?: string;
  isMulti?: boolean;
  /**
   * Executes when the wrapper is blurred.
   */
  onBlur?: (e: React.FocusEvent<HTMLDivElement, Element>) => void;

  ariaLabel?: string;
}

export interface SelectViewProps extends SelectProps {
  disabledOptionValues?: Array<string>;
  required?: boolean;
  label?: string;
  name?: string;
  placeholder?: string;
  displayValue?: string;
  tooltip?: string;
  controls?: React.ReactNode;
  unsafeLabelStyling?: string;
  triggerClassName?: string;
  validation?: boolean;
  validationMsg?: string;
}

interface BasicTriggerProps {
  displayLabel: string;
  placeholder?: string;
  validation?: boolean;
  disabled?: boolean;
}

export const BasicSelectTrigger = (props: BasicTriggerProps) => {
  const isPlaceholder = props.displayLabel === "" || !props.displayLabel;
  const labelID = useSelectViewLabelIDContext();

  return (
    <DropdownMenu.Trigger asChild tabIndex={0} disabled={props.disabled}>
      <div
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={props.disabled ? -1 : 0}
        className={cx(
          " flex items-center justify-between space-x-1 rounded border border-dublin-gray px-4 py-2 text-base focus:outline-none",
          props.placeholder && !props.displayLabel && "text-macbook",
          props.validation && invalidInputOutlineClasses,
          !props.disabled
            ? "bg-white focus:border-flo-magenta focus:ring-1 focus:ring-flo-magenta"
            : "bg-offwhiteVariant",
        )}
        id={labelID}
        aria-label={labelID}
      >
        <span className={"truncate"}>
          {isPlaceholder ? props.placeholder : props.displayLabel}
        </span>
        <span>
          <FiChevronDown
            className={cx(props.disabled ? "text-charcoal opacity-40" : "")}
            size={20}
          />
        </span>
      </div>
    </DropdownMenu.Trigger>
  );
};

export const MULTISELECT_TRIGGER_CLASSES = cx(
  "grid grid-cols-[1fr_auto]",
  "border-dublin-gray justify-between items-center focus:outline-none focus:border-flo-magenta",
  "focus:ring-flo-magenta focus:ring-1 text-base rounded border px-4 py-2 space-x-1",
);
export const MULTISELECT_VALUE_CLASSES =
  "bg-friendly-eskimo rounded text-sm px-1 border border-charcoal-200";

export const MULTISELECT_PLACEHOLDER_CLASSES = "text-macbook";

export interface BasicMultiTriggerProps {
  values: Array<SelectOption>;
  placeholder?: string;
  validation?: boolean;
  disabled?: boolean;
}
export const BasicMultiSelectTrigger = (props: BasicMultiTriggerProps) => {
  const validValues = props.values.filter((value) => value.value !== "");
  const labelID = useSelectViewLabelIDContext();
  return (
    <DropdownMenu.Trigger asChild disabled={props.disabled}>
      <div
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={cx(
          MULTISELECT_TRIGGER_CLASSES,
          props.validation && invalidInputOutlineClasses,
        )}
        id={labelID}
      >
        {validValues?.length > 0 ? (
          <HStack className={"flex-wrap gap-1"}>
            {validValues.map((x) => {
              return (
                <span key={x.value} className={MULTISELECT_VALUE_CLASSES}>
                  {x.label}
                </span>
              );
            })}
          </HStack>
        ) : (
          <span className={MULTISELECT_PLACEHOLDER_CLASSES}>
            {props.placeholder}
          </span>
        )}
        <span className={"block"}>
          <FiChevronDown size={20} className={""} />
        </span>
      </div>
    </DropdownMenu.Trigger>
  );
};

export type AutoSelectViewProps = Omit<
  SelectViewProps,
  "open" | "onOpenChange"
>;
export const AutoSelectView = (props: AutoSelectViewProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const onSelectItem = (item: SelectOption) => {
    if (!props.isMulti) {
      setIsOpen(false);
    }
    props.onSelectItem?.(item);
  };

  return (
    <SelectView
      {...props}
      onSelectItem={onSelectItem}
      open={isOpen}
      onOpenChange={handleOpenChange}
    />
  );
};

export const SelectViewLabelIDContext = createContext("");
export function useSelectViewLabelIDContext() {
  return useContext(SelectViewLabelIDContext);
}

export const SelectView = ({
  required,
  label,
  name,
  placeholder,
  tooltip,
  controls,
  unsafeLabelStyling,
  validation,
  validationMsg,
  ...props
}: SelectViewProps) => {
  const selected = props.options.filter((i) => props.isItemSelected(i));
  const displayValue =
    selected.length > 0 ? selected.map((x) => x.label).join(" ") : "";

  const generatedId = useHtmlId();
  const labelMethod = normalizeLabelProp(label);

  return (
    <SelectViewLabelIDContext.Provider value={label}>
      <div
        className={cx(
          // props.disabled ? "opacity-50 cursor-not-allowed" : "",
          "flex w-full flex-col space-y-2",
        )}
      >
        {label ? (
          <HStack className="items-center justify-between">
            <InputLabel
              id={generatedId}
              labelMethod={labelMethod}
              name={name}
              required={required}
              tooltip={tooltip}
              isElementInteractive={true}
            />
            {controls}
          </HStack>
        ) : null}
        <div aria-label={label}>
          <Select
            {...props}
            fitTrigger
            trigger={() => (
              <React.Fragment>
                {props.isMulti ? (
                  props.trigger ? (
                    props.trigger()
                  ) : (
                    <BasicMultiSelectTrigger
                      placeholder={placeholder}
                      values={selected}
                      validation={validation}
                      disabled={props.disabled}
                    />
                  )
                ) : props.trigger ? (
                  props.trigger()
                ) : (
                  <BasicSelectTrigger
                    placeholder={placeholder}
                    displayLabel={displayValue}
                    validation={validation}
                    disabled={props.disabled}
                  />
                )}
              </React.Fragment>
            )}
          />
        </div>
        <ValidationMessage
          shouldShowValidation={validation}
          validationMessage={validationMsg}
        />
      </div>
    </SelectViewLabelIDContext.Provider>
  );
};

const CreateNewItem = (props: {
  onCreateNewItem: (itemName: string, event: Event) => void;
  itemTitle: string;
  label: string;
}) => {
  return (
    <DropdownMenu.Item
      className={cx(
        "group rounded",
        "focus:bg-friendly-eskimo focus:outline-none",
        "focus:text-navy",
        "hover:text-navy",
        "text-base",
        "cursor-pointer",
      )}
      onSelect={(event) => props.onCreateNewItem(props.itemTitle, event)}
    >
      <div className="flex items-center space-x-2 truncate px-3 py-2 text-navy-400">
        <span>{props.label}</span>
        <span className="ml-2 truncate">{props.itemTitle}</span>
      </div>
    </DropdownMenu.Item>
  );
};

/**
 * @deprecated
 */
export const Select = (props: SelectProps) => {
  const { ref, width = 1 } = useResizeObserver<HTMLElement>();

  const inputRef = useRef<HTMLInputElement>();
  const contentRef = useRef<HTMLDivElement>();
  const [inputValue, setInputValue] = useState("");

  const stayOpen = useRef(false);
  const timeout = useRef<number | undefined>();
  const countdown = () => {
    if (props.hover) {
      stayOpen.current = false;
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = +setTimeout(() => {
        if (!stayOpen.current) {
          props.onOpenChange(false);
        }
      }, 500);
    }
  };

  const style =
    props.width != null
      ? {
          width: props.width,
        }
      : {
          width: props.fitTrigger ? width : "auto",
        };

  const results = props.options?.filter((o) => {
    if (props.filterFunction) {
      return props.filterFunction(o, inputValue);
    }
    return o.label.toLowerCase().includes(inputValue.toLowerCase());
  });

  // If we have a createNewItem function and some sort of title to show
  // This allows us to support new items without the search bar being required
  const hasNewItemTitleOverride = Boolean(props?.newItemOverride?.itemTitle);
  const hasRequiredTitleProps = hasNewItemTitleOverride || props.searchable;
  const hasNewItemHandler = props?.onCreateNewItem !== undefined;

  if (hasNewItemHandler && !hasRequiredTitleProps) {
    throw new Error(errors.newItemHandlerWithoutTitle);
  }

  if (!hasNewItemHandler && hasNewItemTitleOverride) {
    throw new Error(errors.noNewItemHandlerForOveride);
  }

  const hasNewItemTitle = hasNewItemTitleOverride || Boolean(inputValue);
  const showCreateNewItem = hasNewItemHandler && hasNewItemTitle;
  const isOptionDisabled = (option: SelectOption) => {
    if (typeof props?.isOptionDisabled == "function") {
      return props?.isOptionDisabled(option);
    }
    return false;
  };

  const ariaOwns = useMemo(() => {
    return props.options?.map((option) => option.value).join(" ");
  }, [props.options]);

  return (
    <div className="relative" onBlur={props.onBlur}>
      <DropdownMenu.Root
        modal={!props.hover}
        open={props.open}
        onOpenChange={props.onOpenChange}
      >
        <div className="w-full" ref={ref}>
          {props.trigger ? (
            props.trigger()
          ) : (
            <DropdownMenu.Trigger
              tabIndex={0}
              disabled={props.disabled}
              role="button"
              aria-label={props.ariaLabel}
              asChild
            >
              <div
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                onMouseEnter={() => {
                  if (props.hover) {
                    stayOpen.current = true;
                  }
                }}
                onMouseLeave={countdown}
              >
                {props.children}
              </div>
            </DropdownMenu.Trigger>
          )}
        </div>
        <DropdownMenu.Content
          ref={contentRef}
          style={style}
          align={props.align ?? "end"}
          side={props.side ?? "bottom"}
          onMouseEnter={() => {
            if (props.hover) {
              stayOpen.current = true;
            }
          }}
          onMouseLeave={countdown}
          className={cx(
            "space-y-2 rounded-md border border-dublin-gray bg-white shadow",
            props.searchable ? "" : "",
            "mt-1",
          )}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Tab") {
              e.preventDefault();
              inputRef?.current?.focus();
            }
          }}
          aria-owns={ariaOwns}
        >
          {props.searchable ? (
            <div className="px-2 pt-2">
              <input
                aria-label={"searchResults"}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={props.searchable}
                multiple={true}
                ref={inputRef}
                value={inputValue}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Tab") {
                    e.preventDefault();
                    contentRef?.current?.focus();
                  }
                }}
                placeholder={props.placeholder ?? "Search"}
                className="w-full rounded border border-dublin-gray px-4 py-2 text-base text-charcoal"
                onChange={(e) => {
                  setInputValue(e.target.value);
                }}
              />
            </div>
          ) : null}
          <div
            className={cx(
              "max-h-[200px] space-y-1 overflow-auto px-2 pb-2",
              props.searchable ? "pt-1" : "pt-2",
            )}
          >
            {showCreateNewItem ? (
              <CreateNewItem
                onCreateNewItem={props.onCreateNewItem}
                label={props.newItemOverride?.label ?? "Create new item:"}
                itemTitle={props.newItemOverride?.itemTitle ?? inputValue}
              />
            ) : null}
            {results?.map((option) =>
              props.renderItem ? (
                <React.Fragment key={option.key ?? option.value}>
                  {props.renderItem(option)}
                </React.Fragment>
              ) : (
                <BasicOption
                  disabled={isOptionDisabled(option)}
                  key={option.key ?? option.value}
                  item={option}
                  onSelect={props.onSelectItem}
                  selected={props.isItemSelected(option)}
                />
              ),
            )}
            {results?.length === 0 ? (
              <div className="mx-auto max-w-[220px] overflow-hidden truncate py-2 text-center text-sm text-medium-hippo">
                {inputValue.length > 0
                  ? "No results found"
                  : "No options available"}
              </div>
            ) : null}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
};
